// Google Ads conversion tracking - the only module that sends conversions.
//
// The base tag is public/ads-tag.js, loaded once from index.html. It defines
// window.gtag on the live domain only, so every call here is a quiet no-op on
// the test site, on localhost and while pages are pre-rendered.
//
//   trackPurchase(order)       order confirmation, once the server says paid
//   trackQuoteRequest(contact) after /api/quotes accepts a quote request
//   trackQuickQuestion()       after the product-page quick question is sent
//   installPhoneTracking()     one document listener for every tel: link
//
// Every event fires on a confirmed outcome - a successful API response or a
// paid order - never on a button click, so a failed submit is not counted.

import { isPrerendering } from '../prerenderData';

// Must match the ID in public/ads-tag.js.
export const GOOGLE_ADS_ID = 'AW-18478035184';

const SEND_TO = {
  purchase: `${GOOGLE_ADS_ID}/XKgiCMPr2YcdEPDhgetE`,
  quote: `${GOOGLE_ADS_ID}/N6YiCOr-3ocdEPDhgetE`,
  question: `${GOOGLE_ADS_ID}/_u4ACO-s2ocdEPDhgetE`,
  phone: `${GOOGLE_ADS_ID}/bQ0GCN_L4IcdEPDhgetE`,
};

// Returns true when the call reached gtag.
function gtag(...args) {
  if (isPrerendering || typeof window === 'undefined' || typeof window.gtag !== 'function') return false;
  window.gtag(...args);
  return true;
}

// Enhanced conversions: Google matches on a normalised email and an E.164
// phone number, and hashes both itself before they leave the browser.
export function normaliseEmail(email) {
  const e = String(email || '').trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e : '';
}

// Australian numbers as E.164: "0422 439 499" and "61422439499" both become
// "+61422439499". A number already starting with + keeps its country code.
export function normaliseAuPhone(phone) {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) return digits.length >= 8 ? `+${digits}` : '';
  if (digits.startsWith('61') && digits.length === 11) return `+${digits}`;
  if (digits.startsWith('0') && digits.length === 10) return `+61${digits.slice(1)}`;
  return '';
}

function setUserData({ email, phone } = {}) {
  const data = {};
  const e = normaliseEmail(email);
  const p = normaliseAuPhone(phone);
  if (e) data.email = e;
  if (p) data.phone_number = p;
  if (Object.keys(data).length) gtag('set', 'user_data', data);
}

// Purchase. Only for an order the server has marked paid (the Stripe webhook
// does that), and only once per order per browser session: the confirmation
// page can be refreshed, and the flag is keyed on the order ID. Google also
// de-duplicates on transaction_id.
export function trackPurchase(order) {
  if (!order || order.status !== 'paid' || !order.id) return;
  const key = `asg.adsPurchase.${order.id}`;
  try { if (sessionStorage.getItem(key)) return; } catch { /* storage blocked - send once anyway */ }
  setUserData({ email: order.customer_email, phone: order.customer_phone });
  const sent = gtag('event', 'conversion', {
    send_to: SEND_TO.purchase,
    value: Number(order.amount_cents || 0) / 100,
    currency: 'AUD',
    transaction_id: String(order.id),
  });
  if (sent) {
    try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
  }
}

export function trackQuoteRequest(contact = {}) {
  setUserData(contact);
  gtag('event', 'conversion', { send_to: SEND_TO.quote });
}

export function trackQuickQuestion() {
  gtag('event', 'conversion', { send_to: SEND_TO.question });
}

// Every tel: link on the site, present or future, through one listener on the
// document. Capture phase so a component that stops propagation cannot hide a
// click. It never calls preventDefault and waits for nothing, so on a phone
// the call starts immediately; gtag queues the hit and sends it itself.
export function installPhoneTracking() {
  if (typeof document === 'undefined') return () => {};
  const onClick = (e) => {
    const link = e.target && e.target.closest ? e.target.closest('a[href^="tel:"]') : null;
    if (link) gtag('event', 'conversion', { send_to: SEND_TO.phone });
  };
  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}
